/**
 * Intervention and Modifier resolution logic.
 *
 * Medical DATA (MODIFIERS, CAPNO_MORPHOLOGIES, INTERVENTIONS) lives in
 * src/data/ (validated by `npm run check:medical`). This module re-exports it
 * for backward compatibility and holds only the interaction-resolution logic.
 *
 * Interaction model: each intervention has a default `deltas` table plus
 * optional `modifierDeltas` / `scenarioDeltas` overrides keyed by modifier or
 * scenario id. `getEffectiveDeltas` selects the right set for the active context.
 */
import { MODIFIERS, CAPNO_MORPHOLOGIES } from '../data/modifiers';
import { INTERVENTIONS } from '../data/interventions';

export { MODIFIERS, CAPNO_MORPHOLOGIES, INTERVENTIONS };

// ─── Helper functions ─────────────────────────────────────────────────────────

/**
 * Return the effective delta map for an intervention.
 *
 * Priority: modifierDeltas > scenarioDeltas > deltas
 *
 * modifierDeltas wins because the patient's underlying pathophysiology
 * (e.g. hypovolemia) is the most pharmacodynamically constraining context.
 * scenarioDeltas encode the targeted therapeutic effect for specific
 * scenarios (e.g. epinephrine in anaphylaxis normalises capnogram).
 *
 * The returned object may contain a special `capnoMorphology` string key
 * alongside numeric vital deltas — callers should handle it separately.
 */
// `overrideDeltas` is a per-intervention response supplied by an active CUSTOM
// scenario (from its imported JSON). It lets a shared scenario file define how
// each intervention affects it — no app-source hard-coding. Precedence:
// modifierDeltas > custom override > built-in scenarioDeltas > base deltas.
export function getEffectiveDeltas(interventionKey, activeModifier, activeScenario, overrideDeltas = null) {
  const intervention = INTERVENTIONS[interventionKey];
  if (!intervention) return overrideDeltas || {};
  if (activeModifier && intervention.modifierDeltas?.[activeModifier]) {
    return intervention.modifierDeltas[activeModifier];
  }
  if (overrideDeltas) return overrideDeltas;
  if (activeScenario && intervention.scenarioDeltas?.[activeScenario]) {
    return intervention.scenarioDeltas[activeScenario];
  }
  return intervention.deltas;
}

/**
 * Extract the capnoMorphology side-effect (if any) from an intervention's
 * effective delta set.  Returns the morphology string or null.
 * Both modifierDeltas and scenarioDeltas may carry a capnoMorphology key —
 * modifierDeltas are checked first (higher priority), matching getEffectiveDeltas.
 * Base deltas never change the waveform shape directly.
 */
export function getInterventionCapnoMorphology(interventionKey, activeModifier, activeScenario, overrideDeltas = null) {
  const intervention = INTERVENTIONS[interventionKey];
  if (intervention?.modifierDeltas?.[activeModifier]) {
    return intervention.modifierDeltas[activeModifier].capnoMorphology ?? null;
  }
  if (overrideDeltas) return overrideDeltas.capnoMorphology ?? null;
  if (activeScenario && intervention?.scenarioDeltas?.[activeScenario]) {
    return intervention.scenarioDeltas[activeScenario].capnoMorphology ?? null;
  }
  return null;
}

/**
 * Extract the rhythm side-effect (if any) from an intervention's effective
 * delta set.  Returns the rhythm string or null.
 * Both modifierDeltas and scenarioDeltas may carry a rhythm key —
 * modifierDeltas are checked first (higher priority), matching getEffectiveDeltas.
 * Base deltas never change the ECG rhythm directly.
 */
export function getInterventionRhythm(interventionKey, activeModifier, activeScenario, overrideDeltas = null) {
  const intervention = INTERVENTIONS[interventionKey];
  if (!intervention) return overrideDeltas?.rhythm ?? null;
  if (activeModifier && intervention.modifierDeltas?.[activeModifier]) {
    return intervention.modifierDeltas[activeModifier].rhythm ?? null;
  }
  if (overrideDeltas) return overrideDeltas.rhythm ?? null;
  if (activeScenario && intervention.scenarioDeltas?.[activeScenario]) {
    return intervention.scenarioDeltas[activeScenario].rhythm ?? null;
  }
  // Fall back to the base deltas rhythm (e.g. pacemaker always sets 'paced')
  return intervention.deltas?.rhythm ?? null;
}

/**
 * Compute new vital targets after applying an intervention (sign = +1)
 * or reverting one (sign = -1).  Uses vital.target as the base so that
 * simultaneous interventions stack correctly without fighting each other.
 * Non-vital keys like `capnoMorphology` in the delta map are silently skipped.
 */
export function computeInterventionTargets(vitals, interventionKey, activeModifier, sign = 1, activeScenario = null, overrideDeltas = null, scale = 1) {
  return computeInterventionEffect(vitals, interventionKey, activeModifier, sign, activeScenario, overrideDeltas, scale).targets;
}

/**
 * Receptor-style saturation for stacked drugs. A delta that pushes a vital
 * FURTHER beyond its alarm limit (not one correcting a derangement) is
 * compressed linearly from 100 % at the limit to SATURATION_FLOOR at the hard
 * bound, so three pressors on a normotensive patient don't march BP to the
 * display cap. Applied on give only; the exact applied delta is recorded so the
 * revert is always symmetric.
 */
const SATURATION_FLOOR = 0.35;
export function saturateDelta(vital, base, delta) {
  if (!delta) return delta;
  if (delta > 0 && base > vital.alarmHigh && vital.max > vital.alarmHigh) {
    const frac = Math.min(1, (base - vital.alarmHigh) / (vital.max - vital.alarmHigh));
    return delta * (1 - (1 - SATURATION_FLOOR) * frac);
  }
  if (delta < 0 && base < vital.alarmLow && vital.alarmLow > vital.min) {
    const frac = Math.min(1, (vital.alarmLow - base) / (vital.alarmLow - vital.min));
    return delta * (1 - (1 - SATURATION_FLOOR) * frac);
  }
  return delta;
}

/**
 * Like computeInterventionTargets but also returns the exact per-vital delta
 * that was applied (after dose scaling, saturation and clamping), so the
 * caller can store it and revert precisely later via revertAppliedDeltas().
 * `scale` is the dose multiplier (1 = the reference dose in the data layer).
 */
export function computeInterventionEffect(vitals, interventionKey, activeModifier, sign = 1, activeScenario = null, overrideDeltas = null, scale = 1) {
  const deltas = getEffectiveDeltas(interventionKey, activeModifier, activeScenario, overrideDeltas);
  const targets = computeInterventionTargetsRaw(vitals, interventionKey, deltas, sign, scale);
  const applied = {};
  Object.entries(targets).forEach(([k, t]) => {
    const base = vitals[k]?.target ?? vitals[k]?.current;
    if (typeof base === 'number') applied[k] = t - base;
  });
  return { targets, applied };
}

/** Undo a recorded `applied` delta map exactly (clamped to bounds). */
export function revertAppliedDeltas(vitals, applied) {
  const targets = {};
  Object.entries(applied ?? {}).forEach(([k, d]) => {
    const vital = vitals[k];
    if (!vital || typeof d !== 'number') return;
    const base = vital.target ?? vital.current;
    targets[k] = Math.max(vital.min, Math.min(vital.max, base - d));
  });
  return targets;
}

function computeInterventionTargetsRaw(vitals, interventionKey, deltas, sign, scale) {
  const newTargets = {};

  // Special case: transcutaneous pacemaker HR logic varies by clinical context.
  //
  // TdP (overdrive pacing): fixed rate of 105 bpm suppresses re-entry by
  // eliminating long R-R pauses. The pacemaker fires faster than any bradycardic
  // escape, shortening QT and closing the EAD-triggered vulnerable window.
  //
  // All other scenarios (demand pacing): floor at 80 bpm — paces only if the
  // intrinsic rate falls below threshold; allows faster native rates to continue.
  if (interventionKey === 'transcutaneousPacemaker' && sign === 1) {
    const OVERDRIVE_RATE = 105;  // TdP suppression rate
    const DEMAND_RATE    = 80;   // bradycardia floor for all other contexts
    const base = vitals.HR.target ?? vitals.HR.current;
    newTargets.HR = activeScenario === 'tdp'
      ? OVERDRIVE_RATE
      : Math.max(DEMAND_RATE, base);
    // Apply other deltas normally (BP improvements from increased rate)
    Object.entries(deltas).forEach(([vitalName, delta]) => {
      if (vitalName === 'HR') return; // already handled above
      const vital = vitals[vitalName];
      if (!vital) return;
      if (typeof delta !== 'number') return;
      const vitalBase = vital.target ?? vital.current;
      newTargets[vitalName] = Math.max(vital.min, Math.min(vital.max, vitalBase + delta));
    });
    return newTargets;
  }

  // Adenosine in re-entrant SVT: a rapid IV bolus transiently blocks the AV
  // node, producing a brief ASYSTOLIC PAUSE (flat ECG, collapsed output) before
  // the SVT recurs. The per-scenario deltas are authored as the negative of that
  // scenario's baseline, so values crash toward zero on administration and the
  // symmetric revert (sign -1, standard path below) restores the pre-drug SVT
  // targets. We floor at 0 here — not vital.min — so HR/pressures can reach the
  // true flatline and the numerics match the flat waveforms (the rate-driven
  // channels self-flatten once HR < 1; see waveformEngine).
  if (interventionKey === 'adenosine' && sign === 1 && deltas.rhythm === 'asystole') {
    Object.entries(deltas).forEach(([vitalName, delta]) => {
      const vital = vitals[vitalName];
      if (!vital || typeof delta !== 'number') return;
      const base = vital.target ?? vital.current;
      newTargets[vitalName] = Math.max(0, Math.min(vital.max, base + delta));
    });
    return newTargets;
  }

  // Standard delta-based interventions
  Object.entries(deltas).forEach(([vitalName, delta]) => {
    const vital = vitals[vitalName];
    if (!vital) return;   // skips non-vital keys like capnoMorphology
    if (typeof delta !== 'number') return;
    const base = vital.target ?? vital.current;
    const scaled = delta * scale;
    const d = sign > 0 ? saturateDelta(vital, base, scaled) : scaled;
    newTargets[vitalName] = Math.max(vital.min, Math.min(vital.max, base + sign * d));
  });
  return newTargets;
}

/**
 * Compute new ventilator settings after applying (sign = +1) or reverting
 * (sign = -1) an intervention's airway-mechanics side-effect.
 *
 * A delta table may carry `vent: { vte, ppeak, pplat }` — RELATIVE deltas so
 * that stacked drugs (albuterol + epinephrine) and their expiries compose, just
 * like vitals. Bronchodilators mostly lower Ppeak (resistive component) with a
 * small Pplat change; decompressing a tension pneumothorax restores both.
 * Returns null when the effective deltas carry no vent block.
 */
const VENT_DELTA_KEYS = ['vte', 'ppeak', 'pplat'];
// Absolute settings (vaporizer / fresh gas) — applied on give (sign +1) only;
// the panel snapshots and restores the prior values on wear-off / cancel.
export const VENT_ABSOLUTE_KEYS = ['agent', 'agentPct', 'n2o', 'fio2'];
export function computeVentTargets(ventSettings, interventionKey, activeModifier, sign = 1, activeScenario = null, overrideDeltas = null, scale = 1) {
  const vent = getEffectiveDeltas(interventionKey, activeModifier, activeScenario, overrideDeltas)?.vent;
  if (!vent) return null;
  const next = { ...ventSettings };
  VENT_DELTA_KEYS.forEach((k) => {
    if (typeof vent[k] !== 'number') return;
    next[k] = Math.round(next[k] + sign * vent[k] * scale);
  });
  if (sign > 0) {
    VENT_ABSOLUTE_KEYS.forEach((k) => { if (vent[k] !== undefined) next[k] = vent[k]; });
  }
  next.vte = Math.max(0, Math.min(1500, next.vte));
  next.ppeak = Math.max(0, Math.min(80, next.ppeak));
  // Keep the ordering the vent screen expects: PEEP ≤ Pplat ≤ Ppeak.
  next.pplat = Math.max(0, Math.min(next.pplat, next.ppeak));
  next.peep = Math.min(next.peep, next.pplat);
  return next;
}

/**
 * Compute new vital targets for toggling a modifier on (sign = +1) or
 * off (sign = -1).
 */
export function computeModifierTargets(vitals, modifierKey, sign = 1) {
  const modifier = MODIFIERS[modifierKey];
  if (!modifier) return {};
  const newTargets = {};
  Object.entries(modifier.deltas).forEach(([vitalName, delta]) => {
    const vital = vitals[vitalName];
    if (!vital) return;
    const base = vital.target ?? vital.current;
    newTargets[vitalName] = Math.max(vital.min, Math.min(vital.max, base + sign * delta));
  });
  return newTargets;
}

// Interventions always shown regardless of the active scenario (core toolkit).
// Supplemental O₂ is first-line for almost any patient, so it's always offered
// rather than gated on SpO₂ already being below the alarm threshold.
const UNIVERSAL_INTERVENTIONS = new Set([
  'ivFluid', 'intubation', 'venturiMask', 'nonRebreather',
  // Anaesthetic maintenance is part of every case, not a scenario-specific fix.
  'sevoflurane', 'desflurane', 'isoflurane', 'nitrousOxide',
]);

/**
 * Is an intervention clinically relevant to the active scenario? Used to
 * declutter the instructor panel — only drugs that make sense for the current
 * situation are shown (with a "Show all" override in the UI).
 *
 * Relevant if ANY:
 *   • it is in the universal core set, OR
 *   • it defines a modifierDeltas/scenarioDeltas entry for the active
 *     modifier/scenario (explicitly indicated in this context), OR
 *   • one of its base deltas corrects a currently-deranged vital (pushes a
 *     value that is past its alarm threshold back toward normal).
 */
export function isInterventionRelevant(interventionKey, activeScenario, activeModifier, vitals) {
  const iv = INTERVENTIONS[interventionKey];
  if (!iv) return false;
  if (UNIVERSAL_INTERVENTIONS.has(interventionKey)) return true;
  // Explicit indication for this exact context (also covers deliberate
  // contraindication demos) always wins.
  if (activeModifier && iv.modifierDeltas?.[activeModifier]) return true;
  if (activeScenario && iv.scenarioDeltas?.[activeScenario]) return true;

  const deltas = iv.deltas || {};

  // Directional BP guard: don't offer a BP-LOWERING drug to a hypotensive
  // patient, or a BP-RAISING drug to a hypertensive patient — the commonest
  // "wrong drug" that would otherwise slip through the corrective check below.
  const sys = vitals?.NIBP_Sys;
  if (sys) {
    const bpTarget = sys.target ?? sys.current;
    const bpDelta = deltas.NIBP_Sys ?? 0;
    if (bpTarget < sys.alarmLow && bpDelta < 0) return false;
    if (bpTarget > sys.alarmHigh && bpDelta > 0) return false;
  }

  // Corrective: a base delta pushes a currently-deranged vital toward normal.
  for (const [vk, delta] of Object.entries(deltas)) {
    if (typeof delta !== 'number' || delta === 0) continue;
    const v = vitals?.[vk];
    if (!v) continue;
    const t = v.target ?? v.current;
    if (t < v.alarmLow && delta > 0) return true;   // deranged low, drug raises it
    if (t > v.alarmHigh && delta < 0) return true;  // deranged high, drug lowers it
  }
  return false;
}

// ─── Gated recovery ───────────────────────────────────────────────────────────
//
// Some arrest / peri-arrest scenarios stay refractory to standard ACLS until a
// cause-specific antidote has been given AND allowed to circulate. The gate is
// authored on the scenario as:
//   recoveryGate: {
//     antidotes: ['intralipid'],   // intervention key(s) that treat the cause
//     mode: 'any' | 'all',         // how many antidotes must be given+settled
//     settleMs: 45000,             // time-to-work after administration
//     blocks: ['defibrillation', 'epinephrine', …],  // actions with no effect until open
//     hint: '…',                   // instructor guidance shown while closed
//   }
// `antidoteTimes` is a map of interventionKey → earliest administration timestamp.

/**
 * Resolve a recovery gate's current state.
 * @returns {{ open: boolean, anyGiven: boolean, settleRemainingMs: number }}
 */
export function recoveryGateStatus(gate, antidoteTimes = {}, now = Date.now(), settleMs) {
  if (!gate) return { open: true, anyGiven: false, settleRemainingMs: 0 };
  const settle = settleMs != null ? settleMs : (gate.settleMs ?? 0);
  const givenTimes = (gate.antidotes ?? []).map((k) => antidoteTimes[k]).filter((t) => t != null);
  const anyGiven = givenTimes.length > 0;
  const isSettled = (t) => now - t >= settle;

  const open = gate.mode === 'all'
    ? (gate.antidotes ?? []).every((k) => antidoteTimes[k] != null && isSettled(antidoteTimes[k]))
    : givenTimes.some(isSettled);

  let settleRemainingMs = 0;
  if (!open && anyGiven) {
    const remaining = givenTimes.map((t) => settle - (now - t)).filter((r) => r > 0);
    settleRemainingMs = remaining.length ? Math.min(...remaining) : 0;
  }
  return { open, anyGiven, settleRemainingMs };
}

/**
 * Is a recovery action (a shock scenario key or a drug key) currently blocked by
 * the scenario's recovery gate? True only when the action is in `gate.blocks`
 * AND the gate is not yet open.
 */
export function isRecoveryBlocked(gate, actionKey, antidoteTimes = {}, now = Date.now(), settleMs) {
  if (!gate || !gate.blocks?.includes(actionKey)) return false;
  return !recoveryGateStatus(gate, antidoteTimes, now, settleMs).open;
}

/**
 * Dose presets. A drug may declare `doses: [{ label, scale, default? }]`; the
 * scale multiplies its numeric deltas (1 = the reference dose the deltas were
 * written for). Returns the default preset, or null for single-dose drugs.
 */
export function defaultDose(interventionKey) {
  const doses = INTERVENTIONS[interventionKey]?.doses;
  if (!doses?.length) return null;
  return doses.find((d) => d.default) ?? doses[0];
}

/** Format milliseconds remaining as M:SS */
export function formatCountdown(ms) {
  if (ms <= 0) return '0:00';
  const totalSec = Math.ceil(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}
